import React from "react";
import { Link } from "react-router-dom";
import { Flame, Trophy, Zap, Award, ArrowUpRight } from "lucide-react";

/* GamificationStrip — Unstop-style energetic CTA strip with 4 cards.
 * Each card targets a different funnel stage: discover → practice → verify → land. */

const CARDS = [
  {
    key: "sprint",
    title: "100-Day Skill Sprint",
    body: "Daily micro-challenges across 10 CoE labs. Build a public profile, level up.",
    icon: Flame,
    to: "/lab",
    grad: "linear-gradient(135deg, #FF6B35 0%, #FF3D7F 100%)",
    badge: "Most popular",
  },
  {
    key: "practice",
    title: "Code Conquest",
    body: "Timed coding rounds with auto-grade + instant feedback. Climb the weekly leaderboard.",
    icon: Trophy,
    to: "/lab",
    grad: "linear-gradient(135deg, #006CFF 0%, #00B8D4 100%)",
    badge: "New",
  },
  {
    key: "verify",
    title: "Verified Certificates",
    body: "QR-signed credentials any recruiter can verify in 2 seconds. No screenshots, no fakes.",
    icon: Award,
    to: "/verify",
    grad: "linear-gradient(135deg, #9933FF 0%, #5546FF 100%)",
  },
  {
    key: "matched",
    title: "Auto-Matched Roles",
    body: "Our model scores every job against your verified skills + CoE badges. Apply with one tap.",
    icon: Zap,
    to: "/opportunities",
    grad: "linear-gradient(135deg, #10B981 0%, #0099FF 100%)",
  },
];

export default function GamificationStrip() {
  return (
    <section className="bg-paper" data-testid="landing-gamification">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="text-center mb-10">
          <span className="eyebrow-hot">
            <Flame size={11} strokeWidth={2.5} /> Hot on SkillConnect
          </span>
          <h2 className="font-display h-display text-3xl sm:text-4xl lg:text-5xl mt-3">
            Learn out loud. Climb the leaderboard.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-muted max-w-2xl mx-auto leading-relaxed">
            Four built-in ways to stand out. Every action you take builds a verified profile that
            recruiters can trust — and SkillConnect rewards.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {CARDS.map((c) => {
            const Ic = c.icon;
            return (
              <Link
                key={c.key}
                to={c.to}
                data-testid={`gamification-card-${c.key}`}
                className="relative rounded-2xl p-5 sm:p-6 overflow-hidden transition-all duration-500 hover:-translate-y-2 group"
                style={{
                  background: c.grad,
                  boxShadow: "0 22px 44px -22px rgba(0,0,0,0.35)",
                }}
              >
                {/* hover overlay sheen */}
                <span aria-hidden="true"
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: "radial-gradient(60% 60% at 100% 0%, rgba(255,255,255,0.25) 0%, transparent 70%)" }}
                />
                <div className="relative flex items-start justify-between">
                  <div className="w-12 h-12 rounded-xl grid place-items-center bg-white/20 backdrop-blur transition-transform duration-500 group-hover:rotate-[-6deg] group-hover:scale-110">
                    <Ic size={22} strokeWidth={2.2} className="text-white" />
                  </div>
                  {c.badge && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/95 text-ink">
                      {c.badge}
                    </span>
                  )}
                </div>
                <h3 className="relative font-display font-black text-lg sm:text-xl mt-5 text-white leading-tight">
                  {c.title}
                </h3>
                <p className="relative text-xs sm:text-sm text-white/85 mt-2 leading-relaxed">
                  {c.body}
                </p>
                <div className="relative mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/95 group-hover:gap-2.5 transition-all">
                  Get started <ArrowUpRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
